import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LayoutGroup } from 'framer-motion';
import { ArrowClockwise, Check, Coins, Scales, SlidersHorizontal, SquaresFour, Users, WarningCircle, Wrench, type Icon } from '@phosphor-icons/react';
import { ImmoButton, MorphBuehne, MorphPanel, SegmentedControl, WidgetGroup } from '../../../../components/immobilien/ui';
import { ANGEBUNDENE_PANELS, PANEL_KOPF, istPanelKey, type ObjektPanelKey } from '../../../../components/immobilien/objektPanels';
import { objektWidgetsFuer } from '../../../../components/immobilien/objektWidgets';
import { GRUPPEN, gruppenAnzahl, widgetsDerGruppe, type GruppenFilter, type GruppenKey } from '../../../../lib/immobilien/widgetRaster';
import { useWidgetSichtbarkeit } from '../../../../lib/immobilien/widgetSichtbarkeit';
import { useObjektDetail } from '../../../../lib/immobilien/useObjektDetail';
import { einheitenUebersicht, objektKennzahlen } from '../../../../lib/immobilien/objektDetail';
import { objektAdresse, objektName } from '../../../../lib/immobilien/objektKopf';
import { verwaltungsartVonObjekt } from '../../../../lib/immobilien/sidebarNav';
import { heuteIso } from '../../../../lib/immobilien/format';
import { mitViewTransition } from '../../../../lib/immobilien/viewTransition';
import { ObjektSeitenKontext, type ObjektSeitenWert } from './ObjektSeitenKontext';
import { Breadcrumb, ObjektHero } from './ObjektHero';
import { ObjektWidget } from './ObjektWidgets';
import { WidgetBearbeiten } from './WidgetBearbeiten';
import { PanelInhalt } from './panels/PanelInhalt';
import { EinheitenTab } from './einheiten/EinheitenTab';

export const GRUPPEN_ICONS: Record<GruppenKey, Icon> = { ueb: SquaresFour, verm: Users, fin: Coins, tech: Wrench, recht: Scales };

/** Objekt-Detailseite (README §5.2/5.3, Screens 03–16). */
/** bereich: Übersicht (Widgets) oder Einheiten-Tab – beide teilen Kopf, Sidebar und Daten. */
export default function ObjektSeite({ objektId, bereich = 'uebersicht' }: { objektId: string; bereich?: 'uebersicht' | 'einheiten' }) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { zustand, neuLaden } = useObjektDetail(objektId);
  const [ausgeblendet, setAusgeblendet] = useWidgetSichtbarkeit();
  const [filter, setFilter] = useState<GruppenFilter>('alle');
  const [bearbeiten, setBearbeiten] = useState(false);

  const roherPanel = params.get('panel');
  const panel: ObjektPanelKey | null = bereich === 'uebersicht' && istPanelKey(roherPanel) && ANGEBUNDENE_PANELS.has(roherPanel) ? roherPanel : null;
  const panelBearbeiten = params.get('bearbeiten') === '1';

  const oeffnePanel = useCallback((key: ObjektPanelKey, optionen?: { bearbeiten?: boolean }) => {
    const neu = new URLSearchParams();
    neu.set('panel', key);
    if (optionen?.bearbeiten) neu.set('bearbeiten', '1');
    setParams(neu);
  }, [setParams]);

  const schliessePanel = useCallback(() => setParams(new URLSearchParams()), [setParams]);

  const zuEinheiten = useCallback((einheitId?: string) => {
    const basis = `/admin/objekte/${encodeURIComponent(objektId)}/einheiten`;
    mitViewTransition(() => { navigate(einheitId ? `${basis}/${encodeURIComponent(einheitId)}` : basis); window.scrollTo(0, 0); });
  }, [navigate, objektId]);

  const daten = zustand.status === 'ok' ? zustand.daten : null;
  const heute = heuteIso();
  const uebersicht = useMemo(() => (daten ? einheitenUebersicht(daten.einheiten, daten.mieter, heute) : []), [daten, heute]);
  const kennzahlen = useMemo(() => objektKennzahlen(uebersicht, daten?.objekt.wohnflaeche), [uebersicht, daten]);

  const wert: ObjektSeitenWert | null = useMemo(() => (daten && zustand.status === 'ok' ? {
    daten,
    fehler: zustand.fehler,
    name: objektName(daten.objekt),
    adresse: objektAdresse(daten.objekt),
    verwaltungsart: verwaltungsartVonObjekt(daten.objekt),
    uebersicht,
    kennzahlen,
    neuLaden,
    oeffnePanel,
    panelBearbeiten,
    zuEinheiten,
  } : null), [daten, zustand, uebersicht, kennzahlen, neuLaden, oeffnePanel, panelBearbeiten, zuEinheiten]);

  if (zustand.status === 'laedt') {
    return (
      <div aria-busy="true" aria-label="Objekt wird geladen" className="flex flex-col" style={{ gap: 16 }}>
        <div className="animate-pulse" style={{ height: 34, width: 260, borderRadius: 17, background: 'var(--fill2)' }} />
        <div className="animate-pulse" style={{ height: 240, borderRadius: 30, background: 'var(--fill2)' }} />
        <div className="animate-pulse" style={{ height: 44, borderRadius: 16, background: 'var(--fill2)' }} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(150px,1fr))', gridAutoRows: 158, gap: 16 }}>
          {Array.from({ length: 8 }, (_, i) => <div key={i} className="animate-pulse" style={{ borderRadius: 26, background: 'var(--fill2)' }} />)}
        </div>
      </div>
    );
  }
  if (zustand.status === 'fehlt' || zustand.status === 'fehler' || !wert) {
    return (
      <div role="alert" className="immo-glas flex flex-col items-center text-center" style={{ borderRadius: 28, padding: '48px 20px', gap: 12 }}>
        <WarningCircle weight="fill" size={36} color="var(--bad)" />
        <span style={{ fontSize: 18, fontWeight: 800 }}>{zustand.status === 'fehlt' ? 'Objekt nicht gefunden' : 'Objekt konnte nicht geladen werden'}</span>
        {zustand.status === 'fehler' && <span style={{ fontSize: 13, color: 'var(--ink3)' }}>{zustand.meldung}</span>}
        <div className="flex" style={{ gap: 8 }}>
          <ImmoButton onClick={() => navigate('/admin/objekte')}>Zur Objektliste</ImmoButton>
          {zustand.status === 'fehler' && <ImmoButton art="akzent" icon={ArrowClockwise} onClick={() => neuLaden()}>Erneut versuchen</ImmoButton>}
        </div>
      </div>
    );
  }

  const defs = objektWidgetsFuer(wert.verwaltungsart);
  const segmente = [
    { key: 'alle' as const, label: 'Alle', icon: SquaresFour, anzahl: gruppenAnzahl(defs, ausgeblendet, 'o', 'alle') },
    ...GRUPPEN.filter(g => g.key !== 'ueb').map(g => ({ key: g.key as GruppenFilter, label: g.label, icon: GRUPPEN_ICONS[g.key], anzahl: gruppenAnzahl(defs, ausgeblendet, 'o', g.key as GruppenFilter) })),
  ];
  const fehlerBereiche = Object.keys(wert.fehler);
  const kopf = panel ? PANEL_KOPF[panel] : null;

  return (
    <ObjektSeitenKontext.Provider value={wert}>
      <div className="flex flex-col" style={{ gap: 16 }}>
        <Breadcrumb />
        <ObjektHero />

        {fehlerBereiche.length > 0 && (
          <div role="status" className="flex items-center flex-wrap" style={{ gap: 10, padding: '10px 14px', borderRadius: 16, background: 'var(--warn-soft)', color: 'var(--warn)', fontSize: 13, fontWeight: 600 }}>
            <WarningCircle weight="fill" size={18} />
            Einige Bereiche konnten nicht geladen werden ({fehlerBereiche.join(', ')}).
            <button type="button" onClick={() => neuLaden()} className="cursor-pointer" style={{ border: 'none', background: 'transparent', color: 'inherit', fontWeight: 800, textDecoration: 'underline' }}>Erneut laden</button>
          </div>
        )}

        {bereich === 'einheiten' ? <EinheitenTab /> : (<>
        <div className="flex justify-between items-center flex-wrap" style={{ gap: 10 }}>
          <SegmentedControl segmente={segmente} wert={filter} onChange={setFilter} ariaLabel="Widget-Gruppen" />
          <ImmoButton art={bearbeiten ? 'akzent' : 'fuellung'} icon={bearbeiten ? Check : SlidersHorizontal} onClick={() => setBearbeiten(b => !b)} aria-pressed={bearbeiten}
            style={{ fontWeight: 700 }}>
            {bearbeiten ? 'Fertig' : 'Widgets bearbeiten'}
          </ImmoButton>
        </div>

        {bearbeiten && <WidgetBearbeiten scope="o" defs={defs} ausgeblendet={ausgeblendet} onChange={setAusgeblendet} gruppenIcons={GRUPPEN_ICONS} />}

        <LayoutGroup id={`objekt-${objektId}`}>
          <MorphBuehne
            offen={panel !== null}
            panel={panel && kopf ? (
              <MorphPanel layoutId={`objekt-panel-${panel}`} titel={kopf.titel} icon={kopf.icon} onSchliessen={schliessePanel}>
                <PanelInhalt key={`${panel}-${panelBearbeiten ? 'b' : 'a'}`} panel={panel} />
              </MorphPanel>
            ) : null}
          >
            <div className="flex flex-col" style={{ gap: 16 }}>
              {GRUPPEN.map(g => {
                const liste = widgetsDerGruppe(defs, g.key, ausgeblendet, 'o', filter, bearbeiten);
                if (liste.length === 0) return null;
                return (
                  <WidgetGroup key={g.key} titel={g.label} icon={GRUPPEN_ICONS[g.key]} mitUeberschrift={filter === 'alle'}
                    elemente={liste.map(d => ({ key: d.key, groesse: d.groesse, inhalt: <ObjektWidget def={d} gesperrt={bearbeiten} gedimmt={bearbeiten && !!ausgeblendet.o[d.key]} /> }))} />
                );
              })}
            </div>
          </MorphBuehne>
        </LayoutGroup>
        </>)}
      </div>
    </ObjektSeitenKontext.Provider>
  );
}
